import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../contexts/ThemeContext';
import { authAPI } from '../utils/api';
import { NotificationBell } from './NotificationBell';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Calendar,
  Briefcase,
  Users,
  DollarSign,
  BarChart3,
  Settings,
  LogOut,
  X,
  KeyRound,
  Clapperboard,
  CalendarClock,
  Calculator,
  MoreHorizontal,
  Search,
  ChevronLeft,
  Inbox
} from 'lucide-react';
import { GlobalSearch } from './GlobalSearch';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setPwdLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdLoading(false);
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, adminOnly: false },
    { name: 'Leads', href: '/leads', icon: Inbox, adminOnly: true },
    { name: 'Calendar', href: '/calendar', icon: Calendar, adminOnly: false },
    { name: 'Schedule', href: '/schedule', icon: CalendarClock, adminOnly: true },
    { name: 'Projects', href: '/projects', icon: Briefcase, adminOnly: true },
    { name: 'Crew Members', href: '/crew', icon: Users, adminOnly: true },
    { name: 'Payments', href: '/payments', icon: DollarSign, adminOnly: true },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, adminOnly: true },
    { name: 'Finance', href: '/finance', icon: Calculator, adminOnly: true },
    { name: 'Workflow', href: '/workflow', icon: Clapperboard, adminOnly: true },
    { name: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
  ];

  const filteredNavigation = isAdmin ? navigation : navigation.filter(item => !item.adminOnly);

  const isItemActive = (href) =>
    href === '/workflow' ? location.pathname.startsWith('/workflow') : location.pathname === href;

  // Primary items surfaced on the floating mobile tab bar
  const bottomNav = [
    { name: 'Home', href: '/', icon: LayoutDashboard },
    { name: 'Calendar', href: '/calendar', icon: Calendar },
    ...(isAdmin ? [
      { name: 'Crew', href: '/crew', icon: Users },
      { name: 'Payments', href: '/payments', icon: DollarSign },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card/80 backdrop-blur-xl border-b border-border px-4 flex items-center justify-between"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))', paddingBottom: '0.75rem' }}
      >
        <Link to="/">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_83ef92c5-3c5d-4a35-b89a-364f4b1c35a2/artifacts/htwqujap_1000X1000.png"
            alt="Ami Video"
            className="h-9 cursor-pointer dark:brightness-0 dark:invert"
          />
        </Link>
        <div className="flex items-center gap-1">
          {location.pathname !== '/' && (
            <button
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
              className="p-2 rounded-full text-foreground hover:bg-secondary transition-colors"
              data-testid="mobile-back-button"
              aria-label="Go back"
            >
              <ChevronLeft size={22} />
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowSearch(true)}
              className="p-2 rounded-full text-foreground hover:bg-secondary transition-colors"
              data-testid="mobile-search-button"
              aria-label="Search"
            >
              <Search size={20} strokeWidth={1.75} />
            </button>
          )}
          <NotificationBell />
        </div>
      </div>

      <div className="lg:flex">
        {/* Mobile overlay for the More drawer */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-50 w-72 sidebar transform transition-transform duration-300 ease-out lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col">
            <div className="p-6 flex items-center justify-between" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}>
              <Link to="/" className="block" onClick={() => setSidebarOpen(false)}>
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_83ef92c5-3c5d-4a35-b89a-364f4b1c35a2/artifacts/htwqujap_1000X1000.png"
                  alt="Ami Video"
                  className="h-11 cursor-pointer hover:opacity-80 transition-opacity dark:brightness-0 dark:invert"
                />
              </Link>
              <div className="flex items-center gap-1">
                <div className="hidden lg:block"><NotificationBell /></div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-2 rounded-full text-muted-foreground hover:bg-secondary transition-colors"
                  data-testid="mobile-menu-close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
              {isAdmin && (
                <button
                  onClick={() => { setShowSearch(true); setSidebarOpen(false); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-sm text-muted-foreground bg-secondary hover:bg-secondary transition-colors mb-2"
                  data-testid="sidebar-search-button"
                >
                  <Search size={18} strokeWidth={1.75} />
                  <span className="font-medium">Search…</span>
                </button>
              )}
              {filteredNavigation.map((item) => {
                const isActive = isItemActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon size={19} strokeWidth={1.75} />
                    <span className="font-medium tracking-tight">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="p-4 mt-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <div className="bg-secondary rounded-2xl p-4">
                <p className="text-sm font-semibold tracking-tight">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize mb-3">{user?.role}</p>
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Appearance</p>
                  <ThemeToggle />
                </div>
                <button
                  onClick={() => { setShowPasswordModal(true); setSidebarOpen(false); }}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors mb-1"
                  data-testid="change-password-button"
                >
                  <KeyRound size={15} strokeWidth={1.75} />
                  <span className="font-medium">Change Password</span>
                </button>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs text-destructive hover:bg-destructive/10 transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut size={15} strokeWidth={1.75} />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </aside>

        <main className="lg:ml-72 min-h-screen w-full pt-[calc(max(0.75rem,env(safe-area-inset-top))+3.75rem)] lg:pt-0 pb-28 lg:pb-0">
          {children}
        </main>
      </div>

      {/* Floating bottom tab bar (mobile only) */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pointer-events-none"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
      >
        <div className="pointer-events-auto mx-auto max-w-md bg-card/80 backdrop-blur-xl border border-border rounded-full px-2 py-1.5 flex items-center justify-around shadow-[0_8px_32px_rgba(0,0,0,0.14)]">
          {bottomNav.map((item) => {
            const isActive = isItemActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded-full transition-all active:scale-90 ${
                  isActive ? 'text-gold' : 'text-muted-foreground'
                }`}
                data-testid={`bottom-nav-${item.name.toLowerCase()}`}
              >
                <item.icon size={22} strokeWidth={isActive ? 2.25 : 1.75} />
                <span className="text-[10px] font-medium tracking-tight">{item.name}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded-full text-muted-foreground transition-all active:scale-90"
            data-testid="bottom-nav-more"
          >
            <MoreHorizontal size={22} strokeWidth={1.75} />
            <span className="text-[10px] font-medium tracking-tight">More</span>
          </button>
        </div>
      </nav>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowPasswordModal(false)}>
          <div className="bg-card rounded-3xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
            <h2 className="text-2xl font-semibold tracking-tight mb-6">Change Password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-secondary border border-transparent focus:border-ring/40 focus:ring-2 focus:ring-ring/20 text-sm outline-none"
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-secondary border border-transparent focus:border-ring/40 focus:ring-2 focus:ring-ring/20 text-sm outline-none"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-secondary border border-transparent focus:border-ring/40 focus:ring-2 focus:ring-ring/20 text-sm outline-none"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={pwdLoading} className="flex-1 bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-medium transition-all active:scale-[0.97] disabled:opacity-50 text-sm" data-testid="submit-password-change">
                  {pwdLoading ? 'Changing…' : 'Change Password'}
                </button>
                <button type="button" onClick={() => setShowPasswordModal(false)} className="px-6 py-3.5 rounded-full bg-secondary hover:bg-secondary/70 transition-colors text-sm font-medium">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <GlobalSearch open={showSearch} onClose={() => setShowSearch(false)} />
    </div>
  );
};

export default Layout;
